import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, tap, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { getSubdomain } from '../utils/subdomain.util';

export interface ActiveModulesResponse {
  products: string[];
  modules: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ModuleAccessService {
  private readonly STORAGE_KEY = 'tenant_active_modules';
  private readonly API_URL = environment.apiUrlV1;

  private products = new Set<string>();
  private modules = new Set<string>();

  // Observable con el estado completo de módulos (como authState en AuthService)
  private moduleState = new BehaviorSubject<ActiveModulesResponse>({ products: [], modules: [] });
  public moduleState$ = this.moduleState.asObservable();

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.restoreFromStorage();
  }

  /**
   * Carga los módulos activos del tenant desde el backend.
   * Se debe llamar después del login.
   */
  loadFromBackend(): Observable<ActiveModulesResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-tenant-id': getSubdomain() || '',
    });

    return this.http.get<ActiveModulesResponse>(
      `${this.API_URL}/tenant-access/active-modules`,
      { headers }
    ).pipe(
      tap(data => this.load(data)),
      catchError(err => {
        console.error('Error cargando módulos activos:', err);
        return of({ products: [], modules: [] });
      })
    );
  }

  /**
   * Carga productos y módulos en memoria y localStorage.
   */
  load(data: ActiveModulesResponse): void {
    this.products = new Set(data.products);
    this.modules = new Set(data.modules);
    this.saveToStorage(data);
    this.moduleState.next(data);
  }

  // ─── Productos ──────────────────────────────────────

  hasProduct(code: string): boolean {
    return this.products.has(code);
  }

  hasAnyProduct(codes: string[]): boolean {
    return codes.some(c => this.products.has(c));
  }

  getProducts(): string[] {
    return Array.from(this.products);
  }

  // ─── Módulos ────────────────────────────────────────

  hasModule(code: string): boolean {
    return this.modules.has(code);
  }

  hasAnyModule(codes: string[]): boolean {
    return codes.some(c => this.modules.has(c));
  }

  hasAllModules(codes: string[]): boolean {
    return codes.every(c => this.modules.has(c));
  }

  getModules(): string[] {
    return Array.from(this.modules);
  }

  // ─── Limpieza ───────────────────────────────────────

  clear(): void {
    this.products.clear();
    this.modules.clear();
    this.moduleState.next({ products: [], modules: [] });

    if (this.isBrowser) {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  // ─── Persistencia ───────────────────────────────────

  private saveToStorage(data: ActiveModulesResponse): void {
    if (this.isBrowser) {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    }
  }

  private restoreFromStorage(): void {
    if (this.isBrowser) {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        try {
          const data: ActiveModulesResponse = JSON.parse(stored);
          this.products = new Set(data.products);
          this.modules = new Set(data.modules);
          this.moduleState.next(data);
        } catch {
          localStorage.removeItem(this.STORAGE_KEY);
        }
      }
    }
  }

  private get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
